import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Http } from '@angular/http';
import { EsriUserService } from './esri-user.service';
import { ToastrService } from 'ngx-toastr';
import { MappingSystemColumn, SystemColumnOption } from '../components/portal/settings-ninja/ninja-meta/ninja-meta.component';
import { USState } from './appinfo.service';

export type EsriFieldType = 'esriFieldTypeOID' | 'esriFieldTypeGeometry' | 'esriFieldTypeGUID' | 'esriFieldTypeDate' | 'esriFieldTypeString' | 'esriFieldTypeDouble' | 'esriFieldTypeInteger' | 'esriFieldTypeSmallInteger';
export type NinjaFieldType = 'string' | 'number' | 'date' | 'boolean' | null; // Field basic data type that can be consumed in ninja
export interface NinjaEventLayer {
	name: string; // 'AADT'
	id: number;
	description: string;
	lrsNetworkId: number;
	datasetName: string; // AADT, BicycleLane
	featureClassName: string;
	unitsOfMeasure: 'esriMiles'; // TODO
	measurePrecision: number;
	temporalViewDate: 'now'; // todo
	timeZoneOffset: number; //?
	isDataVersioned: boolean;
	versionName: any;
	isRouteEventSource: boolean;
	isStaged: boolean;
	dataFormat: 'esriLRSDateFormatFileGDB'; // todo
	spatialReferenceInfo: {
		wkid;
	}; // todo
	eventIdFieldName: string;
	routeIdFieldName: string;
	routeNameFieldName: string;
	fromMeasureFieldName: string;
	toMeasureFieldName: string;
	fromDateFieldName: string;
	toDateFieldName: string;
	fromReferentLocationFieldName: string;
	toReferentLocationFieldName;
	fromReferentOffsetFieldName: string;
	toReferentOffsetFieldName;
	fromReferentMethodFieldName: string;
	toReferentMethodFieldName: string;
	locErrorFieldName: string;
	canSpanRoutes: boolean;
	hasDerivedNetworkFields: boolean;
	hasReferentLocation: boolean;
	isStationEvent: boolean;
	lrs: {
		id: string;
		name: string;
	};
	parentNetwork: {
		id: number;
		name: string;
		type: 'esriLRSNetworkLayer'; // TODO
	}
	fields: NinjaEventLayerField[]; // layer columns

	// Manually added property
	objectIdFieldName: string;
	eventGeomType: 'Line' | 'Point';
}
export interface NinjaRouteLayer {
	id: string;
	name: string;
	featureClassName: string;
	routeNameFieldName: string;
	fromDateFieldName: string;
	toDateFieldName: string;
	routeIdFields: string[];
	compositeRouteIdFieldName: string;
	versionName: any;
	lrs: {
		id: string;
		name: string;
	}
	spatialReferenceInfo: {
		wkid;
	}; // todo
	fields: NinjaLayerField[]; // layer columns
	// ???????????? routeId field
	// TODO
	// Manually added property
	objectIdFieldName: string;
}
export interface NinjaLayerField {
	name: string;
	type: EsriFieldType;
	alias: string;
	editable: boolean;
	nullable: boolean;
	defaultValue: any;
	domain: EsriDomainTypeRange | EsriDomainTypeCodedValue;
	length: number;
}
export interface NinjaEventLayerField extends NinjaLayerField {
	hiddenInTable?: boolean;
	type_ninja: NinjaFieldType;
}
export interface EsriDomainTypeRange {
	type: 'range';
	name: string;
	range: [number, number]
}
export interface EsriDomainTypeCodedValue {
	type: 'codedValue';
	name: string;
	codedValues: {
		name;
		code;
	}[];
}

export interface NinjaDefaults {
	stateInfo?: USState;
	database?: NinjaDatabaseConfig;
	mapServiceUrl?: string;
	systemColumnsMappingLayerTemplate?: NinjaSystemColumnsTemplate;
	eventLayers?: NinjaDefaultsEventLayersConfig;
	routeLayer?: NinjaDefaultRouteLayerConfig;
}
export interface NinjaDatabaseConfig {
	type?: NinjaDatabaseType;
	databaseName?: string;
	sdeOwner?: string;
	dataOwner?: string;
}
export interface NinjaSystemColumnsTemplate {
	mapping: NinjaSystemColumnsMapping;
}
export interface NinjaDefaultsEventLayersConfig {
	[layerName: string]: NinjaDefaultsEventLayerConfig;
}
export interface NinjaDefaultRouteLayerConfig {
	layerName?: string;
	mapping?: NinjaRouteMapping;
}
export interface NinjaDefaultsEventLayerConfig {
		// 'standard': same as the layerTemplate's mapping // 'missing': there're columns missing mapping
		systemColumns: {
			mappingMethod: NinjaSystemColumnMappingMethod;
			mapping?: NinjaSystemColumnsMapping
		};
		columns: NinjaLayerColumnConfig[];
}
export type NinjaSystemColumnMappingMethod = 'standard' | 'missing' | 'customized';
export type NinjaSystemColumnsMapping = {
	[attr in SystemAttribute]?: string;
}
export type NinjaRouteMapping = {
	[attr in RouteAttribute]?: string;
}
export interface NinjaLayerColumnConfig {
	name: string;
	hidden?: boolean;
	seq?: number;
}
export type NinjaDatabaseType = 'msSql' | 'oracle' | 'postgreSql';

// Ninja setting's submenu config
export interface NinjaSettingConfig {
	hasFooter: boolean; // The sub menu has footer or not
	title: string; // Sub menu's title
}
export type SystemAttribute = 'createdAt' | 'createdBy' | 'modifiedAt' | 'modifiedBy';
export type RouteAttribute = 'routeId' | 'routeName' | 'fromDate' | 'toDate' | 'geom';

@Injectable()
export class SettingsNinjaService {

	// Ninja setting's submenu config
	public settingConfig: BehaviorSubject<NinjaSettingConfig> = new BehaviorSubject(null);

	public ninjaDefaults: NinjaDefaults;

	public eventLayers: NinjaEventLayer[] = [];
	public routeLayer: NinjaRouteLayer;

	constructor(
		private http: Http,
		private toastr: ToastrService,
		private esriUserService: EsriUserService
	) { }

	public getNinjaLayers(mapServiceUrl: string) {
		// mapServiceUrl = 'https://arcgis.linearbench.com/arcgis/rest/services/Ninja_WV/MapServer';
		const url = mapServiceUrl + '/exts/LRSServer/layers';
		return new Promise<{eventLayers: NinjaEventLayer[]; routeLayers: NinjaRouteLayer[]}>((resolve, reject) => {
			this.http.get(url, {
				params: {
					token: this.esriUserService.esriToken,
					f: 'json'
				}
			})
			.subscribe(async (rsp: any) => {
				const json = rsp.json();
				if (json.hasOwnProperty('error')) {
					this.toastr.error(json.error.message);
					console.log(json);
					reject(json);
					return;
				}
				let eventLayers: NinjaEventLayer[] = [];
				let routeLayers: NinjaRouteLayer[] = [];
				if (json.eventLayers) {
					eventLayers = json.eventLayers.filter(l => l.isDataVersioned);
					for (let i = 0; i < eventLayers.length; i++) {
						const l = eventLayers[i];
						l.fields.forEach((f, idx) => {
							// set type_ninja for each field
							const colType = this.convertEsriFieldTypeToNinja(f.type);
							f.type_ninja = colType;
						});
						l.eventGeomType = 'Line'; // TODO: Point ???
					}
					eventLayers.sort((a, b) => {
						if (a.name > b.name) {
							return 1;
						} else if (a.name < b.name) {
							return -1;
						} else {
							return 0;
						}
					});
				}
				if (json.networkLayers) {
					routeLayers = json.networkLayers;
					routeLayers.forEach((l) => {
						l.fields.forEach((f) => {
							if (f.type === 'esriFieldTypeOID') {
								l.objectIdFieldName = f.name;
							} 
						})
					})
				}
				resolve({
					eventLayers: eventLayers,
					routeLayers: routeLayers
				});
				this.eventLayers = eventLayers;
				this.routeLayer = routeLayers[0];

			}, (err) => {
				reject(err);
			});
		});
	}

	public getSystemColumnsMappingAndOptions(layerName?, layerConfig?: NinjaDefaultsEventLayerConfig, mapping?: NinjaSystemColumnsMapping, excludeCols?: string[]): { systemColumns: MappingSystemColumn[]; systemColumnOptions: SystemColumnOption[]; } {
		const layer = this.eventLayers.find(l => l.name === layerName) || null;
		// Mapping
		if (layer) {
			const standardMapping = this.ninjaDefaults.systemColumnsMappingLayerTemplate ? this.ninjaDefaults.systemColumnsMappingLayerTemplate.mapping : {};
			let method: NinjaSystemColumnMappingMethod; // Default mapping method
			if (layerConfig && layerConfig.systemColumns) {
				method = layerConfig.systemColumns.mappingMethod;
			}
			method = method || 'standard';
			if (method === 'standard') {
				mapping = standardMapping;
				if (mapping) {
					// Check if all the mapped columns exist in layer
					Object.keys(mapping).forEach(key => {
						const mappedCol = mapping[key];
						const field = layer.fields.find(f => f.name === mappedCol);
						if (!field) {
							mapping[key] = null;
						}
					});
				}
			} else if (method === 'customized') {
				mapping = layerConfig.systemColumns.mapping;
			}
		}
		mapping = mapping || {};
		// System columns
		const systemColumns: MappingSystemColumn[] = [
			{
				attrValue: 'createdAt',
				attrDisplay: 'Created At',
				type: 'esriFieldTypeDate',
				mappedCol: mapping.createdAt
			}, {
				attrValue: 'createdBy',
				attrDisplay: 'Created By',
				type: 'esriFieldTypeString',
				mappedCol: mapping.createdBy
			}, {
				attrValue: 'modifiedAt',
				attrDisplay: 'Modified At',
				type: 'esriFieldTypeDate',
				mappedCol: mapping.modifiedAt
			}, {
				attrValue: 'modifiedBy',
				attrDisplay: 'Modified By',
				type: 'esriFieldTypeString',
				mappedCol: mapping.modifiedBy
			}
		];
		// Options
		const sysOptions = [];
		if (layer) {
			// Default exclude lrs columns
			excludeCols = excludeCols || [
				layer.fromMeasureFieldName,
				layer.toMeasureFieldName,
				layer.fromReferentLocationFieldName,
				layer.toReferentLocationFieldName,
				layer.fromReferentMethodFieldName,
				layer.toReferentMethodFieldName,
				layer.fromReferentOffsetFieldName,
				layer.toReferentOffsetFieldName,
				layer.routeIdFieldName,
				layer.fromDateFieldName,
				layer.toDateFieldName
			];
			layer.fields.forEach(f => {
				// Exclude lrs cols
				if (excludeCols.find(l => l === f.name)) {
					return;
				}
				else if (f.type !== 'esriFieldTypeGeometry' && f.type !== 'esriFieldTypeOID') {
					const col = JSON.parse(JSON.stringify(f));
					sysOptions.push(col);
				}	
			});
		}
		return {
			systemColumnOptions: sysOptions,
			systemColumns: systemColumns
		}
	}

	// Get layer name from featureClassName
	public getLayerNameFromFcName(fcName) {
		// Construct layer name // e.g. [RAH5].[DBO].[LRSE_AADT]
		const splits = fcName.split('.');
		let layerName = '';
		splits.forEach((s) => layerName += '[' + s + '].');
		layerName = layerName.substr(0, layerName.length - 1);
		return layerName;
	}
	public getSdeOwnerFromLayerMeta(layer: NinjaRouteLayer | NinjaEventLayer) {
		if (layer.versionName) {
			const splits = layer.versionName.split('.');
			const sdeOwner = splits[0].replace('\"', '');
			return sdeOwner;
		}
		// TODO!!!!
		else {
			return 'DBO';
		}
	}

	public convertEsriFieldTypeToNinja(fieldType: EsriFieldType): NinjaFieldType {
		// set type_ninja for each field
		let colType: NinjaFieldType;
		switch (fieldType) {
			case 'esriFieldTypeString':
			case 'esriFieldTypeGUID': colType = 'string'; break;
			case 'esriFieldTypeDate': colType = 'date'; break;
			case 'esriFieldTypeDouble':
			case 'esriFieldTypeInteger':
			case 'esriFieldTypeSmallInteger': colType = 'number';
		}
		return colType;
	}
}


// {
//     stateInfo?: {
//         state: string;
//         stateFp: string;
//         code: string;
//         bbox: string;
//     };
//     database?: {
//         type?: NinjaDatabaseType;
//         databaseName?: 'msSql' | 'oracle' | 'postgreSql';
//         sdeOwner?: string;
//         dataOwner?: string;
//     };
//     systemColumnsMappingLayerTemplate?: {
//         mapping: {
//             createdAt: string;
//             createdBy: string;
//             modifiedAt: string;
//             modifiedBy: string;
//         }
//     };
//     eventLayers?: {
//         [layerName: string]: {
//             systemColumns: {
//                 mappingMethod: 'standard' | 'missing' | 'customized';
//                 mapping?: {
//                     createdAt: string;
//                     createdBy: string;
//                     modifiedAt: string;
//                     modifiedBy: string;
//                 }
//             };
//             columns: {
//                 name: string;
//                 hidden?: boolean;
//                 seq?: number;
//             }[];
//         };
//     };
//     routeLayer: {
//         name: string;
//         routeIdCol: string;
//         routeNameCol: string;
//         bDateCol: string;
//         eDateCol: string;
//         geomCol: string;
//     };
//     mapServiceUrl?: string;
// }


// WEBPACK FOOTER //
// ./src/app/common-services/settings-ninja.service.ts